import { type MouseEventHandler, memo, type ReactNode } from 'react';

import { ClipType } from '@air/api/types';
import { type IconComponent, LayersIcon, MessageCircleIcon } from '@air/design-icons';

import { toWorkspaceAgentPreviewCardPlaceholderProps } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/toWorkspaceAgentPreviewCardPlaceholderProps';
import { WorkspaceAgentAssetPreviewMediaFallback } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentAssetPreviewMediaFallback';
import { WorkspaceAgentAssetTypeIcon } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentAssetTypeIcon';
import {
  WorkspaceAgentPreviewCardFrame,
  type WorkspaceAgentPreviewCardFrameProps,
} from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentPreviewCardFrame';
import { WorkspaceAgentPreviewCardMetaRow } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentPreviewCardMetaRow';
import { getWorkspaceAgentAssetMetaLabel } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentAssetMetaLabel';
import { type WorkspaceAgentAssetPreviewCardContent } from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentAssetPreviewCardContent';

type WorkspaceAgentAssetPreviewCardCount = {
  icon: IconComponent;
  label: string;
};

type WorkspaceAgentAssetPreviewCardCounts = {
  commentCount: number | null;
  versionCount: number | null;
};

const getAssetCounts = ({
  commentCount,
  versionCount,
}: WorkspaceAgentAssetPreviewCardCounts): WorkspaceAgentAssetPreviewCardCount[] => {
  const counts: WorkspaceAgentAssetPreviewCardCount[] = [];

  // Every clip has a first version, so a count of one is not worth a line.
  if (versionCount && versionCount > 1) {
    counts.push({ icon: LayersIcon, label: `${versionCount} versions` });
  }

  if (commentCount) {
    counts.push({ icon: MessageCircleIcon, label: commentCount === 1 ? '1 comment' : `${commentCount} comments` });
  }

  return counts;
};

type WorkspaceAgentAssetPreviewCardFrameInput = WorkspaceAgentAssetPreviewCardContent & {
  onBodyMouseDown?: MouseEventHandler<HTMLButtonElement>;
  onOpen: () => void;
};

const toAssetFrameProps = ({
  commentCount,
  duration,
  ext,
  height,
  locationLabel,
  onBodyMouseDown,
  onOpen,
  size,
  thumbnailUrl,
  title,
  type,
  versionCount,
  width,
}: WorkspaceAgentAssetPreviewCardFrameInput): WorkspaceAgentPreviewCardFrameProps => {
  const counts = getAssetCounts({ commentCount, versionCount });

  return {
    children: (
      <>
        <WorkspaceAgentPreviewCardMetaRow
          icon={<WorkspaceAgentAssetTypeIcon size="xs" type={type} />}
          testId="WORKSPACE_AGENT_ASSET_PREVIEW_META"
        >
          {getWorkspaceAgentAssetMetaLabel({ duration, ext, height, size, type, width })}
        </WorkspaceAgentPreviewCardMetaRow>
        {counts.length > 0 ? (
          // Each count leads with its own glyph, so the row itself takes none.
          <WorkspaceAgentPreviewCardMetaRow testId="WORKSPACE_AGENT_ASSET_PREVIEW_COUNTS">
            <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              {counts.map(({ icon: Icon, label }) => (
                <span className="inline-flex shrink-0 items-center gap-1" key={label}>
                  <Icon aria-hidden size="xs" />
                  {label}
                </span>
              ))}
            </span>
          </WorkspaceAgentPreviewCardMetaRow>
        ) : null}
      </>
    ),
    // Only over a still: on the bare type glyph the scrim would bury what it sits on.
    hasPlayOverlay: type === ClipType.video && thumbnailUrl !== null,
    locationLabel,
    locationTitle: null,
    mediaFallback: <WorkspaceAgentAssetPreviewMediaFallback ext={ext} type={type} />,
    mediaImageUrl: thumbnailUrl,
    onBodyMouseDown,
    onOpen,
    openTestId: 'WORKSPACE_AGENT_ASSET_PREVIEW_OPEN',
    testId: 'WORKSPACE_AGENT_ASSET_PREVIEW_CARD',
    title,
  };
};

export type WorkspaceAgentAssetPreviewCardProps = {
  /** Passed in so the card stays free of popover context. */
  closeButton: ReactNode;
  /** `null` until the asset resolves, which is also what the missing state reads as. */
  content: WorkspaceAgentAssetPreviewCardContent | null;
  /** Still in flight, as opposed to settled and missing. */
  isLoading: boolean;
  /** A composer host guards the press so the editor keeps focus and selection. */
  onBodyMouseDown?: MouseEventHandler<HTMLButtonElement>;
  onOpen: () => void;
  /** Shown on hover of the unavailable placeholder, for reporting a bad reference. */
  referenceId: string;
};

/** The asset half of the preview card API: a host hands it a subject, and it draws every state of it. */
export const WorkspaceAgentAssetPreviewCard = memo(
  ({ closeButton, content, isLoading, onBodyMouseDown, onOpen, referenceId }: WorkspaceAgentAssetPreviewCardProps) => {
    let frameProps: WorkspaceAgentPreviewCardFrameProps = toWorkspaceAgentPreviewCardPlaceholderProps({
      label: 'Loading asset',
    });

    if (content) {
      frameProps = toAssetFrameProps({ ...content, onBodyMouseDown, onOpen });
    } else if (!isLoading) {
      frameProps = toWorkspaceAgentPreviewCardPlaceholderProps({
        isUnavailable: true,
        label: 'Unavailable asset',
        referenceId,
      });
    }

    // One frame across all three states: a swap would unmount the close button Radix autofocused on open.
    return <WorkspaceAgentPreviewCardFrame {...frameProps} closeButton={closeButton} />;
  },
);

WorkspaceAgentAssetPreviewCard.displayName = 'WorkspaceAgentAssetPreviewCard';
